import { useEffect, useMemo, useState } from 'react';
import { mindtimeWazuhEndpointsApi } from 'src/api/mindtime/wazuhEndpointsApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { WazuhPagedData } from 'src/types/mindtime/wazuhDashboard';
import type { WazuhEndpointItem } from 'src/types/mindtime/wazuhEndpoints';
import logger from 'src/utils/logger';
import { buildEndpointsApiScope } from '../shared/wazuhScopeParams';

export type EndpointsFilters = {
  pageOrganizationId?: string;
  selectedEndpointId?: string;
  system?: string;
  status?: string;
  agentStatus?: string;
  riskLevel?: string;
};

export type EndpointsSortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type EndpointsState = {
  loading: boolean;
  items: WazuhPagedData<WazuhEndpointItem> | null;
};

const emptyState = (): EndpointsState => ({
  loading: true,
  items: null,
});

export const useEndpointsData = (
  page = 0,
  pageSize = 15,
  search = '',
  filters: EndpointsFilters = {},
  refreshKey = 0,
  sortParams: EndpointsSortParams | null = null
): EndpointsState => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<EndpointsState>(emptyState);

  const { pageOrganizationId, selectedEndpointId, system, status, agentStatus, riskLevel } = filters;

  const listScope = useMemo(
    () =>
      buildEndpointsApiScope({
        scope,
        profileOrgId,
        isReseller,
        pageOrganizationId,
      }),
    [scope, profileOrgId, isReseller, pageOrganizationId]
  );

  const scopeKey = useMemo(() => JSON.stringify(listScope), [listScope]);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    const resolvedSearch = selectedEndpointId || search || undefined;
    const listParams = {
      ...listScope,
      page,
      pageSize,
      ...(resolvedSearch ? { search: resolvedSearch } : {}),
      ...(system ? { system } : {}),
      ...(status ? { status } : {}),
      ...(agentStatus ? { agentStatus } : {}),
      ...(riskLevel ? { riskLevel } : {}),
      ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
    };

    mindtimeWazuhEndpointsApi
      .list(listParams)
      .then((items) => {
        if (cancelled) return;
        setState({ loading: false, items });
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useEndpointsData');
          setState({ loading: false, items: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listScope,
    scopeKey,
    page,
    pageSize,
    search,
    selectedEndpointId,
    system,
    status,
    agentStatus,
    riskLevel,
    refreshKey,
    sortParams?.column,
    sortParams?.sort,
  ]);

  return state;
};
